import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";

// Augment TanStack Query's `meta` shape so call-sites can pass
// `meta: { skipSentry: true }` to opt out of the global Sentry capture
// hooks below without using `as`/`any`.
declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: { skipSentry?: boolean };
    mutationMeta: { skipSentry?: boolean };
  }
}

import { captureSentryException } from "@blis/telemetry/client";
import { isExpectedTrpcCode } from "@blis/telemetry/lib/expected-errors";
import {
  createTRPCQueryUtils,
  httpBatchLink,
  httpLink,
  loggerLink,
  splitLink,
  superjson,
  type TRPCClientErrorRouter,
  trpc,
} from "@blis/trpc/react";

import { toast } from "@/components/ui/toast";
import { debouncedLogout } from "@/features/auth/api";
import { getApiUrl } from "@/lib/api";

const url = getApiUrl("/api/trpc");

/**
 * Per-query / per-mutation opt-out for Sentry capture. Set
 * `meta: { skipSentry: true }` on a `useQuery` / `useMutation` call when the
 * caller already handles the error path (e.g. shows its own toast) and
 * doesn't want to also create a Sentry Issue. Useful for known noisy
 * surfaces like background pollers or fire-and-forget mutations.
 */
function shouldSkipSentry(meta: Record<string, unknown> | undefined): boolean {
  return meta?.skipSentry === true;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(_err, query) {
      const err = _err as never as TRPCClientErrorRouter;
      const code = err.data?.code;
      if (code === "UNAUTHORIZED") {
        debouncedLogout(true);
      } else if (code === "FORBIDDEN") {
        toast.error("Forbidden", {
          description: "You do not have permission to perform that operation.",
        });
      }

      // Forward unexpected (5xx-style) errors to Sentry. Expected codes —
      // 401/403/404/etc. — are normal user-visible flows and would just be
      // noise. The server already drops these via `beforeSend`, but doing
      // it client-side too saves a network round-trip and covers errors
      // that never reached the server (network failures, 502s).
      if (!isExpectedTrpcCode(code) && !shouldSkipSentry(query.options.meta)) {
        captureSentryException(err, {
          tags: {
            area: "react-query",
            kind: "query",
            trpc_code: code ?? "unknown",
          },
          contexts: {
            react_query: {
              query_key: JSON.stringify(query.queryKey),
              status: query.state.status,
            },
          },
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError(_err, _vars, _ctx, mutation) {
      const err = _err as never as TRPCClientErrorRouter;
      const code = err.data?.code;
      if (isExpectedTrpcCode(code)) return;
      if (shouldSkipSentry(mutation.options.meta)) return;
      captureSentryException(err, {
        tags: {
          area: "react-query",
          kind: "mutation",
          trpc_code: code ?? "unknown",
        },
        contexts: {
          react_query: {
            mutation_key: mutation.options.mutationKey
              ? JSON.stringify(mutation.options.mutationKey)
              : null,
          },
        },
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000,
      retry(failureCount, _err) {
        const err = _err as never as TRPCClientErrorRouter;
        const code = err.data?.code;
        if (
          code === "BAD_REQUEST" ||
          code === "FORBIDDEN" ||
          code === "UNAUTHORIZED" ||
          code === "NOT_FOUND"
        ) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

const trpcClient = trpc.createClient({
  // transformer: superjson,
  links: [
    loggerLink({
      enabled() {
        return import.meta.env.DEV;
      },
    }),
    splitLink({
      // Realtime invalidation goes over our own SSE bridge
      // (see `@/lib/realtime`), not tRPC subscriptions — so the only
      // remaining split is "skipBatch?" for surfaces (e.g. background
      // polling) that shouldn't pile onto the shared batch request.
      condition: (op) => op.context.skipBatch === true,
      true: httpLink({
        url,
        fetch: credentialedFetch,
        transformer: superjson,
      }),
      false: httpBatchLink({
        url,
        fetch: credentialedFetch,
        transformer: superjson,
      }),
    }),
  ],
});

export function QueryProvider({ children }: { children: React.ReactNode }) {
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}

export const apiUtils = createTRPCQueryUtils({
  queryClient,
  client: trpcClient,
});

function credentialedFetch(
  url: URL | string | RequestInfo,
  options?: RequestInit,
) {
  return fetch(url, {
    ...options,
    credentials: "include",
  });
}
